// Per-question validation rules. Returns null if valid, or an i18n-key string
// if not. The Next button is disabled when any required field returns non-null.

import SECTION_SCHEMA, {
  getFormStepKeys,
  isExperimentalContentStep,
  isOptionalStep,
  resolveStepFields
} from "../schema/sections"
import { evalShowIf, visibleQuestions } from "./showIf"
import { getValueAt } from "./valuePath"

// Accept a URL with OR without the http(s):// protocol (e.g. "website.com" or
// "https://website.com/terms"); QuestionField prepends https:// on blur and the
// backend normalises too, so requiring the protocol just trips users up.
export const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Mirrors sectionProgress.isAnswered. 0 counts as an answer ONLY when the
// schema question opts in via `acceptZero: true` (e.g. refund window = 0
// means "no refunds offered"). For everything else, 0 is treated as unset.
const isEmpty = (v, q) => {
  if (v == null) return true
  if (typeof v === "string") {
    const t = v.trim()
    if (t === "") return true
    if (/^-?0+(\.0+)?$/.test(t)) return !q?.acceptZero
    return false
  }
  if (typeof v === "number") return v === 0 ? !q?.acceptZero : false
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === "object") {
    if ("s3_key" in v || "url" in v) return !(v.s3_key || v.url)
    if ("number" in v) return !v.number
    return Object.values(v).every((x) => isEmpty(x))
  }
  return false
}

export const validateQuestion = (question, value) => {
  // Required-but-empty wins over everything.
  if (question.required && isEmpty(value, question)) {
    // The dot matters: this repo's locale files store dot-terminated keys NESTED (i18next
    // keySeparator), so the bare "This field is required" resolves to an object and t() renders
    // "key ... returned an object instead of string" right in the form.
    return "This field is required."
  }
  if (isEmpty(value, question)) return null // optional + empty = OK

  switch (question.type) {
    case "url":
      if (!URL_RE.test(String(value).trim())) {
        return "Enter a valid URL, for example website.com"
      }
      return null
    case "text":
      if (question.key === "support_email") {
        if (!EMAIL_RE.test(String(value))) return "Enter a valid email address"
      }
      return null
    case "textarea": {
      const len = String(value).trim().length
      const min = question.min ?? 20
      if (question.required && len < min) {
        // i18n key with a placeholder; QuestionField passes { min } to t().
        return "Please write at least _min_ characters"
      }
      return null
    }
    case "number": {
      const n = Number(value)
      if (Number.isNaN(n)) return "Enter a valid number"
      if (n < 0) return "Can't be negative"
      return null
    }
    case "phone": {
      const num = typeof value === "object" ? value?.number : value
      if (!num || String(num).replace(/[^\d]/g, "").length < 6) {
        return "Enter a valid phone number"
      }
      return null
    }
    case "select":
    case "chip-multi":
    case "upload":
    default:
      return null
  }
}

// A descriptor auto-verified from the connected processor lives at
// building_blocks.processors[slug].statement_descriptor and renders as a LOCKED chip that
// is filtered out of the editable list, so the field can be genuinely answered with zero
// editable entries. sectionProgress already knew this; validation did not, which left the
// Billing descriptors field showing its verified chips while still failing "required" and
// blocking Next. Exported so sectionProgress imports it instead of keeping a second copy
// that can drift again.
export const hasVerifiedProcessorDescriptor = (buildingBlocks) => {
  const procs = buildingBlocks?.processors
  if (!procs || typeof procs !== "object") return false
  const connected = Array.isArray(procs.connected) ? procs.connected : []
  return connected.some((slug) => procs[slug] && procs[slug].statement_descriptor)
}

// Is this question satisfied by something other than its own stored value? Two cases:
// a chip-multi with `allowOther` answered only via custom chips in `<key>_custom`, and a
// processor-verified descriptor list. Both must count as filled for validation and for the
// Next gate, exactly as they already do for the progress counter.
const hasCustomChip = (sectionState, q, buildingBlocks) => {
  if (
    q.type === "text-multi" &&
    q.verifiedFrom === "processors" &&
    hasVerifiedProcessorDescriptor(buildingBlocks)
  )
    return true
  if (q.type !== "chip-multi" || !q.allowOther) return false
  const customs = getValueAt(sectionState, `${q.key}_custom`)
  return Array.isArray(customs) && customs.length > 0
}

export const validateSection = (sectionKey, sectionState, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  const errors = {}
  for (const q of schema) {
    const v = getValueAt(sectionState, q.key)
    if (q.required && isEmpty(v, q) && hasCustomChip(sectionState, q, buildingBlocks)) continue
    const err = validateQuestion(q, v)
    if (err) errors[q.key] = err
  }
  return errors
}

const FORMAT_EXEMPT_TYPES = ["textarea", "select", "chip-multi", "upload"]

export const omitInvalidValues = (sectionKey, sectionState) => {
  const invalidHeads = new Set()
  for (const q of SECTION_SCHEMA[sectionKey] || []) {
    if (FORMAT_EXEMPT_TYPES.includes(q.type)) continue
    const v = getValueAt(sectionState, q.key)
    if (!isEmpty(v, q) && validateQuestion(q, v)) invalidHeads.add(q.key.split(".")[0])
  }
  if (invalidHeads.size === 0) return sectionState
  return Object.fromEntries(Object.entries(sectionState).filter(([k]) => !invalidHeads.has(k)))
}

export const sectionHasAnyAnswer = (sectionKey, sectionState, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  return schema.some(
    (q) =>
      !isEmpty(getValueAt(sectionState, q.key), q) || hasCustomChip(sectionState, q, buildingBlocks)
  )
}

// Next is disabled when:
//   - any VISIBLE required field is missing or invalid, OR
//   - any visible non-required field is filled with an invalid value, OR
//   - the section has zero answered questions (so the user can't blow through
//     an entirely-optional section like Your defenses without touching anything).
// Evidence Vault is the exception: uploads are optional + the integrations
// grid is "connect what you have", so Next is always enabled there.
// EXPERIMENTAL: the isNextEnabled equivalent for a step that aggregates fields across
// buckets. Same rules (required must be filled + valid, filled optionals must be valid),
// applied over the step's resolved fields with each field read from its own section
// bucket. The "at least one answered" rule applies only when the step actually has a
// required field (see below).
export const isStepNextEnabled = (stepKey, buildingBlocks) => {
  // All-optional step (the Evidence locker): never blocks Next, same as the standard
  // wizard's evidence_vault exception.
  if (isOptionalStep(stepKey)) return true
  const fields = resolveStepFields(stepKey).filter((f) => evalShowIf(f.def.showIf, buildingBlocks))
  let anyAnswered = false
  let anyRequired = false
  for (const f of fields) {
    const sectionState = buildingBlocks?.[f.section] ?? {}
    const q = f.def
    const v = getValueAt(sectionState, q.key)
    const customsFilled = hasCustomChip(sectionState, q, buildingBlocks)
    const filled = !isEmpty(v, q) || customsFilled
    if (filled) anyAnswered = true
    if (q.required) {
      anyRequired = true
      if (!filled) return false
      if (!isEmpty(v, q) && validateQuestion(q, v)) return false
    } else if (!isEmpty(v, q) && validateQuestion(q, v)) {
      return false
    }
  }
  // "Answer at least one thing" only makes sense when the step HAS required fields to
  // point at. On a step where every visible field is optional (e.g. Authorization and
  // Consent, whose 8 fields carry no asterisk) this rule was a dead end: Next refused,
  // no field was marked, and the message talked about required fields that don't exist.
  // A merchant with nothing to declare there must be able to move on.
  if (anyRequired && fields.length > 0 && !anyAnswered) return false
  return true
}

// Why Next is blocked, so the message can be truthful:
//   'required'  - a visible required field is empty or invalid (the form highlights them)
//   'untouched' - nothing answered on an all-optional section (nothing to highlight)
//   null        - not blocked
export const nextBlockReason = (stepKey, buildingBlocks) => {
  const expStep = isExperimentalContentStep(stepKey)
  const enabled = expStep
    ? isStepNextEnabled(stepKey, buildingBlocks)
    : isNextEnabled(stepKey, buildingBlocks?.[stepKey] || {}, buildingBlocks)
  if (enabled) return null
  const missingRequired = expStep
    ? resolveStepFields(stepKey)
        .filter((f) => evalShowIf(f.def.showIf, buildingBlocks))
        .some((f) => {
          const st = buildingBlocks?.[f.section] ?? {}
          return (
            f.def.required &&
            isEmpty(getValueAt(st, f.def.key), f.def) &&
            !hasCustomChip(st, f.def, buildingBlocks)
          )
        })
    : visibleQuestions(SECTION_SCHEMA[stepKey] || [], buildingBlocks).some((q) => {
        const st = buildingBlocks?.[stepKey] ?? {}
        return (
          q.required && isEmpty(getValueAt(st, q.key), q) && !hasCustomChip(st, q, buildingBlocks)
        )
      })
  return missingRequired ? "required" : "untouched"
}

export const isNextEnabled = (sectionKey, sectionState, buildingBlocks) => {
  if (sectionKey === "evidence_vault") return true
  // Enrichment is "connect what you have": connecting a tool is optional, so Next is
  // always enabled (nothing to complete before moving on).
  if (sectionKey === "enrichment") return true
  // Integrate requires a connected payment processor (the dispute sync + verified
  // statement descriptor depend on it): it is NOT optional. This gate is intentionally
  // redundant with required_information's processor gate (required_information owns
  // the requirement); it governs the Next affordance while the user is physically on
  // the Integrate step.
  if (sectionKey === "integrations") {
    const connected = buildingBlocks?.processors?.connected
    return Array.isArray(connected) && connected.length > 0
  }
  // The payment processor is connected in the Integrate step, not required_information,
  // so required_information is gated only on its own fields. Activation still requires
  // a connected processor: it is enforced on the Integrate step (above) and in the
  // Footer's activate gate (canActivate).
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  let anyAnswered = false
  for (const q of schema) {
    const v = getValueAt(sectionState, q.key)
    const customsFilled = hasCustomChip(sectionState, q, buildingBlocks)
    const filled = !isEmpty(v, q) || customsFilled
    if (filled) anyAnswered = true
    if (q.required) {
      if (!filled) return false
      if (!isEmpty(v, q) && validateQuestion(q, v)) return false
    } else if (!isEmpty(v, q) && validateQuestion(q, v)) {
      return false
    }
  }
  // Section with visible questions but nothing answered → block Next.
  if (schema.length > 0 && !anyAnswered) return false
  return true
}

// Is a single step passable, whichever kind it is? Content steps aggregate fields
// across buckets (isStepNextEnabled); everything else keeps the per-section rule.
export const isStepComplete = (stepKey, buildingBlocks) =>
  isExperimentalContentStep(stepKey)
    ? isStepNextEnabled(stepKey, buildingBlocks)
    : isNextEnabled(stepKey, buildingBlocks?.[stepKey] || {}, buildingBlocks)

// Every form step of the active flow passes: no required field anywhere is empty or
// invalid. Gates the rail's forward jumps, Finish, and activation.
export const allStepsComplete = (buildingBlocks) =>
  getFormStepKeys().every((k) => isStepComplete(k, buildingBlocks))

// The first step that still needs attention (null when everything passes). Used to
// tell the merchant where to go and to bound how far forward the rail can jump.
export const firstIncompleteStep = (buildingBlocks) =>
  getFormStepKeys().find((k) => !isStepComplete(k, buildingBlocks)) || null
